import * as React from 'react';
import {
  validateGlobalPhone,
  validateCzechPhone,
  formatCzechPhone,
  formatGlobalPhone,
} from '@mc/react-format';

import { StringInput } from '../../string/StringInput';
import { FormItem } from './FormItem';
import { Field } from '../Field';

import type { FieldRenderProps } from 'react-final-form';

import type { VFFieldProps } from '../types';
import { DATA_FORM_INPUT } from '../../common/data';

export type PhoneType = 'czech' | 'global';

const getValidationForPhoneType = (
  type: PhoneType
): ((phone?: string | undefined) => string | undefined) => {
  switch (type) {
    case 'czech':
      return validateCzechPhone;
    default:
      return validateGlobalPhone;
  }
};

const getFormattingFunctionForPhoneType = (
  type: PhoneType
): ((phone: string) => string | undefined) => {
  switch (type) {
    case 'czech':
      return formatCzechPhone;
    default:
      return formatGlobalPhone;
  }
};

export type PhoneProps = VFFieldProps<
  React.ComponentProps<typeof StringInput>
> & {
  phoneType: PhoneType;
};
export const Phone = React.forwardRef<HTMLInputElement, PhoneProps>(
  (
    {
      name,
      label,
      phoneType = 'global',
      validate: baseValidation,
      className,
      inputClassName,
      contentClassName,
      inlineValidation,
      onChange: customOnChange,
      onBlur: customOnBlur,
      required,
      disabled,
      validateWarning,
      ...other
    },
    ref
  ) => {
    const validation = React.useCallback(
      (value: any): string | Promise<any> | undefined => {
        return baseValidation
          ? baseValidation(value)
          : getValidationForPhoneType(phoneType)(value);
      },
      [phoneType, baseValidation]
    );

    const prevSentValue = React.useRef<string>();

    return (
      <FormItem
        name={name}
        label={label}
        inlineValidation={inlineValidation}
        className={className}
        required={required}
        disabled={disabled}
        validateWarning={validateWarning}
        contentClassName={contentClassName}
      >
        <Field
          name={name}
          validate={validation}
          formatOnBlur={true}
          format={getFormattingFunctionForPhoneType(phoneType)}
        >
          {({
            input: { value, onChange, onBlur, name, ...nother },
            meta,
          }: FieldRenderProps<string>) => {
            const [displayValue, setDisplayValue] = React.useState<
              string | undefined
            >(value);

            React.useEffect(() => {
              if (value !== prevSentValue.current) {
                prevSentValue.current = value;
                setDisplayValue(value);
              }
            }, [value]);

            const handleChange = React.useCallback(
              (e: React.ChangeEvent<HTMLInputElement>) => {
                const untrimmedValue = e.target.value;
                setDisplayValue(untrimmedValue);

                let realVal: string | undefined = untrimmedValue?.trim?.();
                if (realVal === '') {
                  realVal = undefined;
                }
                // Store the trimmed value so the effect knows this came from user input
                prevSentValue.current = realVal;
                const rv = { target: { name, value: realVal } };
                customOnChange
                  ? customOnChange(realVal as any)
                  : onChange?.(rv);
              },
              [customOnChange, onChange, name]
            );

            const handleOnBlur = React.useCallback(
              (e: React.FocusEvent<HTMLInputElement>) => {
                const untrimmedValue = e.target.value;
                let realVal: string | undefined = untrimmedValue?.trim?.();
                if (realVal === '') {
                  realVal = undefined;
                }

                setDisplayValue(realVal);
                prevSentValue.current = realVal;
                // Ensure form state is trimmed on blur (handles initial values
                // with whitespace that the user never typed into)
                const rv = { target: { name, value: realVal } };
                customOnChange ? customOnChange(realVal as any) : onChange?.(rv);
                customOnBlur ? customOnBlur(e) : onBlur?.(e);
              },
              [customOnBlur, onBlur, customOnChange, onChange, name]
            );

            return (
              <StringInput
                {...DATA_FORM_INPUT(name)}
                className={inputClassName}
                // @ts-ignore
                ref={ref}
                {...nother}
                name={name}
                {...other}
                disabled={disabled || meta.submitting}
                onChange={handleChange}
                onBlur={handleOnBlur}
                value={displayValue ?? ''}
              />
            );
          }}
        </Field>
      </FormItem>
    );
  }
);
